import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

export interface PomodoroConfig {
  studyMinutes: number;
  shortBreakMinutes: number;
  longBreakMinutes: number;
  pomodorosUntilLongBreak: number;
}

interface PomodoroSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PomodoroConfig;
  onConfigChange: (config: PomodoroConfig) => void;
}

const STORAGE_KEY = 'pomodoro_config';

export function loadPomodoroConfig(): PomodoroConfig {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return getDefaultConfig();
    }
  }
  return getDefaultConfig();
}

export function savePomodoroConfig(config: PomodoroConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

function getDefaultConfig(): PomodoroConfig {
  return {
    studyMinutes: 25,
    shortBreakMinutes: 5,
    longBreakMinutes: 15,
    pomodorosUntilLongBreak: 4,
  };
}

export function PomodoroSettings({
  open,
  onOpenChange,
  config,
  onConfigChange,
}: PomodoroSettingsProps) {
  const [localConfig, setLocalConfig] = useState<PomodoroConfig>(config);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleSave = () => {
    onConfigChange(localConfig);
    savePomodoroConfig(localConfig);
    onOpenChange(false);
  };

  const handleReset = () => {
    const defaultConfig = getDefaultConfig();
    setLocalConfig(defaultConfig);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurações do Pomodoro</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Study time */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Tempo de estudo</Label>
              <span className="text-sm font-medium">{localConfig.studyMinutes} min</span>
            </div>
            <Slider
              value={[localConfig.studyMinutes]}
              onValueChange={([value]) => setLocalConfig({ ...localConfig, studyMinutes: value })}
              min={5}
              max={60}
              step={5}
            />
          </div>

          {/* Short break */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Pausa curta</Label>
              <span className="text-sm font-medium">{localConfig.shortBreakMinutes} min</span>
            </div>
            <Slider
              value={[localConfig.shortBreakMinutes]}
              onValueChange={([value]) => setLocalConfig({ ...localConfig, shortBreakMinutes: value })}
              min={1}
              max={15}
              step={1}
            />
          </div>

          {/* Long break */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Pausa longa</Label>
              <span className="text-sm font-medium">{localConfig.longBreakMinutes} min</span>
            </div>
            <Slider
              value={[localConfig.longBreakMinutes]}
              onValueChange={([value]) => setLocalConfig({ ...localConfig, longBreakMinutes: value })}
              min={5}
              max={30}
              step={5}
            />
          </div>

          {/* Pomodoros until long break */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Pomodoros até pausa longa</Label>
              <span className="text-sm font-medium">{localConfig.pomodorosUntilLongBreak}</span>
            </div>
            <Slider
              value={[localConfig.pomodorosUntilLongBreak]}
              onValueChange={([value]) => setLocalConfig({ ...localConfig, pomodorosUntilLongBreak: value })}
              min={2}
              max={6}
              step={1}
            />
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={handleReset}>
            Restaurar Padrões
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
